// client/src/inquiries/InquiryList.tsx
import React, { FC, Fragment, useCallback, SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
    List, Datagrid, TextField,
    useListContext, TextInput, FunctionField, ListProps,
    DateInput, FormDataConsumer, ReferenceInput, SelectInput, BooleanInput, useDataProvider,
} from 'react-admin';
import { Tabs, Tab, Button, Link as MuiLink, Box } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { INQUIRIES_MODULE_ROLES } from '../roles/portalRoles';
import { IInquiry } from '../types/types';
import { fetchInquirySummaries, InquiryWorkflowSummary, pollTwilioForInquiryReplies } from './inquiryWorkflowApi';

// 🔹 Status tabs
const tabs = [
    { id: '1', name: 'Pending' },
    { id: '2', name: 'Approved' },
    { id: '3', name: 'Rejected' },
];

const formatInquiryAmount = (value: number | string | null | undefined, fractionDigits = 0) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return numeric.toLocaleString('en-IN', {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
    });
};

type SubCategoryChoice = {
    id: number;
    sub_category_name: string;
};

const SubCategorySelectInput: FC<{ categoryId: number; subCategoryId?: number }> = ({
    categoryId,
    subCategoryId,
}) => {
    const [subCategories, setSubCategories] = useState<SubCategoryChoice[]>([]);
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (!categoryId) {
            setSubCategories([]);
            return;
        }

        dataProvider
            .getList('subcategories/bycategory', {
                filter: { category_id: categoryId, is_virgin: true },
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'sub_category_name', order: 'ASC' },
            })
            .then(({ data }) => setSubCategories(data as SubCategoryChoice[]))
            .catch((error) => {
                console.error('Failed to load inquiry filter subcategories', error);
                setSubCategories([]);
            });
    }, [categoryId, dataProvider]);

    return (
        <SelectInput
            source="sub_category_id"
            label="Sub Category"
            choices={subCategories}
            optionText="sub_category_name"
            optionValue="id"
            defaultValue={subCategoryId}
            alwaysOn
        />
    );
};

// 🔹 Inquiry filters
const inquiryFilters = [
    <ReferenceInput
        key="category-filter"
        source="category_id"
        reference="categories"
        label="Category"
        filter={{ is_virgin: true }}
        alwaysOn
    >
        <SelectInput optionText="category_name" />
    </ReferenceInput>,
    <FormDataConsumer key="subcategory-filter" alwaysOn>
        {({ formData }) =>
            formData.category_id ? (
                <SubCategorySelectInput
                    categoryId={formData.category_id}
                    subCategoryId={formData.sub_category_id}
                />
            ) : null
        }
    </FormDataConsumer>,
    <TextInput key="product-search" label="Search" source="product_search" resettable alwaysOn />,
    <TextInput label="Customer Number" source="customer_contact" alwaysOn />,
    <BooleanInput
        key="reply-awaited-filter"
        source="reply_awaited_only"
        label="Reply awaited"
        defaultValue={false}
        alwaysOn
    />,
    <TextInput label="Product Name" source="product_name" />,
    <DateInput label="Inquiry Date" source="inquiry_date" key="inquiry-date" />,
];

const getProductLabel = (record: IInquiry) => {
    const name = record.product_name?.trim() || '—';
    const grade = record.product_grade?.trim();
    return grade ? `${name} (${grade})` : name;
};

const getTotalLabel = (record: IInquiry) => {
    const total = formatInquiryAmount(record.inquiry_total, 0);
    const qty = formatInquiryAmount(record.inquiry_quantity, 0);
    const price = formatInquiryAmount(record.inquiry_price, 2);
    if (!total && !qty && !price) return '—';
    if (!qty || !price) return total || '—';
    return `${total || '—'} (${qty} @ ${price})`;
};

const InquiryStatusDot: FC<{ record: IInquiry; summary?: InquiryWorkflowSummary }> = ({
    record,
    summary,
}) => {
    const customerApproved =
        summary?.customer_approved === true || record.customer_approved === true;
    const pendingReply = Boolean(summary?.buyer_response_pending);

    if (customerApproved) {
        return (
            <Box
                component="span"
                title="Customer approved — wants to buy"
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    flexShrink: 0,
                }}
            />
        );
    }
    if (pendingReply) {
        return (
            <Box
                component="span"
                title="Buyer WhatsApp awaiting Credco reply"
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    flexShrink: 0,
                }}
            />
        );
    }
    return null;
};

const InquiryIdLink: FC<{ record: IInquiry; summary?: InquiryWorkflowSummary }> = ({
    record,
    summary,
}) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <InquiryStatusDot record={record} summary={summary} />
        <MuiLink
            component={RouterLink}
            to={`/inquiries/${record.id}`}
            underline="hover"
            onClick={(event) => event.stopPropagation()}
            sx={{ fontWeight: 600, fontSize: 'inherit' }}
        >
            {record.id}
        </MuiLink>
    </Box>
);

const InquiryRepliesButton: FC<{
    record: IInquiry;
    summary?: InquiryWorkflowSummary;
    onOpen: (inquiryId: number) => void;
}> = ({ record, summary, onOpen }) => {
    const label = summary
        ? `${summary.reply_count} / ${summary.broadcast_count}`
        : 'View';
    return (
        <Button
            size="small"
            variant="text"
            onClick={(event) => {
                event.stopPropagation();
                onOpen(record.id);
            }}
        >
            {label}
        </Button>
    );
};

const TabbedDatagrid = () => {
    const { filterValues, setFilters, displayedFilters, data, refetch } = useListContext();
    const navigate = useNavigate();
    const [summaries, setSummaries] = useState<Map<number, InquiryWorkflowSummary>>(new Map());
    const pollInFlight = useRef(false);
    const clearedLegacyReplyFilter = useRef(false);

    // Drop reply_awaited from the old Yes/No dropdown (persisted in RaStore) so the switch is the only control.
    useEffect(() => {
        if (clearedLegacyReplyFilter.current) return;
        if (filterValues.reply_awaited == null) {
            clearedLegacyReplyFilter.current = true;
            return;
        }
        clearedLegacyReplyFilter.current = true;
        const { reply_awaited: _legacy, ...rest } = filterValues;
        setFilters(rest, displayedFilters);
    }, [filterValues, displayedFilters, setFilters]);

    const loadSummaries = useCallback(() => {
        const rows = (data || []) as IInquiry[];
        const ids = rows.map((row) => row.id).filter(Boolean);
        if (!ids.length) {
            setSummaries(new Map());
            return;
        }
        fetchInquirySummaries(ids)
            .then((result) => {
                setSummaries(
                    new Map(result.map((row) => [Number(row.inquiry_id), row] as const))
                );
                if (result.some((row) => row.approval_synced)) {
                    refetch();
                }
            })
            .catch((error) => {
                console.error('Failed to load inquiry reply summaries', error);
            });
    }, [data, refetch]);

    useEffect(() => {
        loadSummaries();
    }, [loadSummaries]);

    const pollTwilio = useCallback(async () => {
        if (pollInFlight.current) return;
        pollInFlight.current = true;
        try {
            await pollTwilioForInquiryReplies(1);
            loadSummaries();
        } catch (error) {
            console.error('Failed to poll Twilio for inquiry replies', error);
        } finally {
            pollInFlight.current = false;
        }
    }, [loadSummaries]);

    // DB reply counts refresh often; Twilio pull is infrequent (webhook covers prod).
    useEffect(() => {
        const DB_POLL_MS = 45000;
        const TWILIO_POLL_MS = 3 * 60 * 1000;
        const dbTick = () => {
            if (document.visibilityState === 'hidden') return;
            loadSummaries();
        };
        const twilioTick = () => {
            if (document.visibilityState === 'hidden') return;
            pollTwilio();
        };
        const dbTimer = window.setInterval(dbTick, DB_POLL_MS);
        const twilioTimer = window.setInterval(twilioTick, TWILIO_POLL_MS);
        const onFocus = () => loadSummaries();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.clearInterval(dbTimer);
            window.clearInterval(twilioTimer);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [loadSummaries, pollTwilio]);

    const handleChange = useCallback(
        (_event: SyntheticEvent, value: string) => {
            setFilters({ ...filterValues, status: value }, displayedFilters);
        },
        [filterValues, setFilters, displayedFilters]
    );

    useAuthorize(INQUIRIES_MODULE_ROLES);

    const openReplies = useCallback(
        (inquiryId: number) => {
            navigate(`/inquiries/${inquiryId}/replies`);
        },
        [navigate]
    );

    return (
        <Fragment>
            <Tabs
                value={filterValues.status || '1'}
                onChange={handleChange}
                centered
                style={{ marginBottom: 10 }}
            >
                {tabs.map(choice => (
                    <Tab key={choice.id} label={choice.name} value={choice.id} />
                ))}
            </Tabs>

            <Datagrid rowClick={false}>
                <FunctionField
                    label="ID"
                    sortBy="id"
                    render={(record: IInquiry) => (
                        <InquiryIdLink
                            record={record}
                            summary={summaries.get(Number(record.id))}
                        />
                    )}
                />
                <TextField source="inquiry_date" label="Inquiry Date" />
                <TextField source="customer_contact" label="Customer" />
                <FunctionField
                    label="Product"
                    sortBy="product_name"
                    render={(record: IInquiry) => getProductLabel(record)}
                />
                <FunctionField
                    label="Total (quantity @ price)"
                    sortBy="inquiry_total"
                    render={(record: IInquiry) => getTotalLabel(record)}
                />
                <FunctionField
                    label="Replies"
                    render={(record: IInquiry) => (
                        <InquiryRepliesButton
                            record={record}
                            summary={summaries.get(Number(record.id))}
                            onOpen={openReplies}
                        />
                    )}
                />
            </Datagrid>
        </Fragment>
    );
};

export const InquiryList = (props: ListProps) => (
    <List
        {...props}
        filters={inquiryFilters}
        filterDefaultValues={{ status: '1', reply_awaited_only: false }}
    >
        <TabbedDatagrid />
    </List>
);

export default InquiryList;
